import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import TakePicIcon from '@assets/icons/TakePicIcon';
import { isTablet } from 'react-device-detect';
import { useSellerStore } from '@stores';
import {
  CardCaptureStepEnum,
  DocumentCaptureStepEnum,
  cardCaptureType,
  AssetTypesEnum
} from '@types';
import { GeolocationManager, getAssetSpecificContent } from '@utils';

interface CardCaptureProps {
  type: cardCaptureType;
  primaryColor: string;
  setLoadImage: (image: any) => void;
  vehiclePosition?: number;
  setStep: (step: number) => void;
}

const CardCapture: React.FC<CardCaptureProps> = ({
  type,
  vehiclePosition,
  primaryColor,
  setLoadImage,
  setStep
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showVideo, setShowVideo] = useState(false);
  const {
    vehicle,
    setVinImage,
    setNumberPlateImage,
    setOdometerImage,
    setOperatingHoursImage,
    setVehicleImageFront,
    setVehicleImageDriver,
    setVehicleImageBack,
    setVehicleImagePassenger,
    setBankImage,
    assetType
  } = useSellerStore();
  const startCamera = async () => {
    try {
      // Stop existing stream if any
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { exact: 'environment' } }
      });
      const canvas = canvasRef.current;
      if (videoRef.current && canvas) {
        videoRef.current.srcObject = stream;
        // Ensure video plays
        videoRef.current.play().catch((error) => {
          console.error('Error playing video:', error);
        });
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  // Check if stream is active
  const isStreamActive = () => {
    if (videoRef.current) {
      const stream = videoRef.current.srcObject as MediaStream;
      if (stream) {
        return stream.getTracks().some((track) => track.readyState === 'live');
      }
    }
    return false;
  };

  const stopCamera = () => {
    if (videoRef.current) {
      const stream = videoRef.current.srcObject as MediaStream;
      if (stream != null) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
      videoRef.current.srcObject = null;
    }
  };
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const capturePicture = () => {
    // Check if stream is active before capturing
    if (!isStreamActive()) {
      console.warn('Camera stream is not active, restarting camera...');
      startCamera();
      return;
    }

    if (canvasRef.current && videoRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context && video.readyState >= 2) {
        // readyState 2 = HAVE_CURRENT_DATA, 3 = HAVE_FUTURE_DATA, 4 = HAVE_ENOUGH_DATA
        try {
          const putX = canvas.width - 32;
          context.drawImage(video, 72, 110, putX, 250, 0, 0, putX, 250);
          const imageData = context.getImageData(0, 0, putX, 250);
          canvas.width = putX;
          canvas.height = 250;
          context.putImageData(imageData, 0, 0);
          const capturedImage = canvas.toDataURL('image/jpeg');
          setCapturedImage(capturedImage);

          const geolocationManager = GeolocationManager.getInstance();
          geolocationManager.startBackgroundLocationRequest();

          stopCamera();
          setLoadImage(capturedImage);
          setStep(2);
        } catch (error) {
          console.error('Error capturing image:', error);
          // Try to restart camera if capture fails
          startCamera();
        }
      } else {
        console.warn('Video not ready, waiting for stream...');
        // Wait a bit and try again
        setTimeout(() => {
          if (isStreamActive() && video.readyState >= 2) {
            capturePicture();
          } else {
            startCamera();
          }
        }, 500);
      }
    }
  };

  useLayoutEffect(() => {
    setShowVideo(true);
    if (type === CardCaptureStepEnum.vin) {
      setVinImage({
        image: '',
        vin: '',
        available: true,
        isDone: false
      });
    } else if (type === CardCaptureStepEnum.plateNumber) {
      setNumberPlateImage({
        image: '',
        isDone: false,
        available: true,
        plateNumber: ''
      });
    } else if (type === CardCaptureStepEnum.odometer) {
      setOdometerImage({
        image: '',
        odometer: '',
        available: true,
        isDone: false
      });
    } else if (type === CardCaptureStepEnum.operatingHours) {
      setOperatingHoursImage({
        image: '',
        available: true,
        isDone: false
      });
    } else if (type === DocumentCaptureStepEnum.bank) {
      setBankImage({
        image: '',
        available: true,
        isDone: false
      });
    } else if (
      type === CardCaptureStepEnum.vehiclePhoto &&
      vehiclePosition === 1
    ) {
      setVehicleImageFront({
        imageFront: '',
        imageDriver: '',
        imageBack: '',
        imagePassenger: '',
        type: 'VEHICLE',
        vehiclePosition: 1,
        available: true,
        isDone: false
      });
    } else if (
      type === CardCaptureStepEnum.vehiclePhoto &&
      vehiclePosition === 2
    ) {
      setVehicleImageDriver({
        ...vehicle,
        imageDriver: '',
        imageBack: '',
        type: 'VEHICLE',
        available: true,
        imagePassenger: '',
        vehiclePosition: 2,
        isDone: false
      });
    } else if (
      type === CardCaptureStepEnum.vehiclePhoto &&
      vehiclePosition === 3
    ) {
      setVehicleImagePassenger({
        ...vehicle,
        imagePassenger: '',
        available: true,
        imageBack: '',
        type: 'VEHICLE',
        vehiclePosition: 3,
        isDone: false
      });
    } else if (
      type === CardCaptureStepEnum.vehiclePhoto &&
      vehiclePosition === 4
    ) {
      setVehicleImageBack({
        ...vehicle,
        imageBack: '',
        type: 'VEHICLE',
        available: true,
        vehiclePosition: 4,
        isDone: false
      });
    } else if (type === CardCaptureStepEnum.assetFront) {
      setVehicleImageFront({
        image: '',
        available: true,
        isDone: false
      });
    } else if (type === CardCaptureStepEnum.assetLeft) {
      setVehicleImagePassenger({
        image: '',
        available: true,
        isDone: false
      });
    } else if (type === CardCaptureStepEnum.assetRight) {
      setVehicleImageDriver({
        image: '',
        available: true,
        isDone: false
      });
    } else if (type === CardCaptureStepEnum.assetBack) {
      setVehicleImageBack({
        image: '',
        available: true,
        isDone: false
      });
    }

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = 'rgba(0, 0, 0, 1)';
        ctx.beginPath();
        ctx.roundRect(16, 120, canvas.width - 32, isTablet ? 650 : 250, 20);
        ctx.stroke();
        ctx.fill();

        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.roundRect(16, 120, canvas.width - 32, isTablet ? 650 : 250, 20);
        ctx.stroke();
        ctx.fill();
      }
    }
    startCamera();

    // Cleanup function
    return () => {
      stopCamera();
    };
  }, [capturedImage]);

  // Handle visibility change (when Safari goes to background/foreground)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible again, restart camera if it's not active
        if (!isStreamActive() && showVideo) {
          console.log('Page visible, restarting camera...');
          startCamera();
        }
      } else {
        // Page became hidden, but don't stop camera (iOS will handle it)
        console.log('Page hidden');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showVideo]);

  const renderInstructions = () => {
    if (type === CardCaptureStepEnum.vin)
      return 'Please fit your VIN into the frame.';
    if (type === CardCaptureStepEnum.plateNumber)
      return 'Please fit your number plate into the frame.';
    if (type === CardCaptureStepEnum.odometer)
      return 'Please fit the odometer into the frame';
    if (type === DocumentCaptureStepEnum.bank)
      return 'Please fit just the header section of your bank statement into the frame.';
    if (type === CardCaptureStepEnum.operatingHours)
      return 'Please fit the operating hours into the frame.';

    if (type === CardCaptureStepEnum.vehiclePhoto) {
      const assetTitle = getAssetSpecificContent(
        assetType as AssetTypesEnum
      ).lowerTitle;
      if (vehiclePosition === 1)
        return `Please fit the front of the ${assetTitle} into the frame.`;
      if (vehiclePosition === 2)
        return `Please fit the driver's side into the frame.`;
      if (vehiclePosition === 3)
        return `Please fit the passenger side into the frame.`;
      if (vehiclePosition === 4) return `Please fit the back into the frame.`;
    }

    if (type === CardCaptureStepEnum.assetFront)
      return 'Please fit the front of the asset into the frame.';
    if (type === CardCaptureStepEnum.assetLeft)
      return 'Please fit the passenger side of the asset into the frame.';
    if (type === CardCaptureStepEnum.assetRight)
      return 'Please fit the driver side of the asset into the frame.';
    if (type === CardCaptureStepEnum.assetBack)
      return 'Please fit the back of the asset into the frame.';
    if (type === CardCaptureStepEnum.internalFront)
      return 'Please fit the front seats of the vehicle into the frame.';
    if (type === CardCaptureStepEnum.internalBack)
      return 'Please fit the back seats of the vehicle into the frame.';
    if (type === CardCaptureStepEnum.internalBoot)
      return 'Please fit the boot of the vehicle into the frame.';
    if (type === CardCaptureStepEnum.internalLivingArea)
      return 'Please fit the living area of the caravan into the frame.';
    if (type === CardCaptureStepEnum.internalKitchen)
      return 'Please fit the kitchen of the caravan into the frame.';
    if (type === CardCaptureStepEnum.internalSleepingArea)
      return 'Please fit the sleeping area of the caravan into the frame.';

    return '';
  };

  return (
    <>
      <div
        className="bg-opacity-40 flex flex-col w-screen h-full mt-[-12px] ml-[-16px] overflow-y-hidden no-scrollbar tablet-portrait:absolute tablet-portrait:left-0 tablet-portrait:top-0 tablet-portrait:ml-0"
        style={{ height: window.innerHeight - 56 }}>
        <div className="flex w-full h-full">
          {showVideo && (
            <video
              ref={videoRef}
              height={window.innerHeight}
              style={{
                height: window.innerHeight,
                zIndex: 20
              }}
              autoPlay
              playsInline
              className="webcam-feed"
            />
          )}
          <canvas
            ref={canvasRef}
            width={'361px'}
            height={window.innerHeight}
            style={{
              width: '100vw',
              border: 'none',
              zIndex: 20,
              backdropFilter: 'blur(10px)',
              height: window.innerHeight,
              position: 'absolute'
            }}
          />
        </div>
        <div
          className={clsx(
            'px-4 absolute z-100 inset-x-0 flex flex-col items-center justify-center gap-2',
            window.innerHeight > 700 ? 'bottom-[56px]' : 'bottom-[12px]'
          )}>
          <div className="text-white text-center">{renderInstructions()}</div>
          <button onClick={capturePicture}>
            <TakePicIcon color={primaryColor} />
          </button>
        </div>
      </div>
    </>
  );
};

export default CardCapture;
